class ChapterVideo {
  constructor() {
    this.blocks = Array.prototype.slice.call(document.querySelectorAll('[data-chapter-video]'));

    if (!this.blocks.length) {
      return;
    }

    this.blocks.forEach((block) => this.init(block));
  }

  init(block) {
    const playBtn = block.querySelector('[data-chapter-video-play]');
    const poster = block.querySelector('.lc-chapter-video__poster');
    const video = block.querySelector('video');
    const markers = Array.prototype.slice.call(block.querySelectorAll('[data-chapter-video-marker]'));

    if (video) {
      video.removeAttribute('autoplay');
      video.removeAttribute('muted');
      video.muted = false;
      video.autoplay = false;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.setAttribute('controls', '');

      video.addEventListener('timeupdate', () => this.syncActiveMarker(video, markers));
      video.addEventListener('play', () => block.classList.add('is-playing'));
      video.addEventListener('pause', () => {
        if (video.ended) {
          return;
        }
        block.classList.remove('is-playing');
      });
      video.addEventListener('ended', () => block.classList.remove('is-playing'));
    }

    const start = (event) => {
      if (event) {
        event.preventDefault();
      }
      this.play(block);
    };

    if (playBtn) {
      playBtn.addEventListener('click', start);
    }

    if (poster) {
      poster.addEventListener('click', start);
    }

    markers.forEach((marker) => {
      marker.addEventListener('click', () => {
        const seconds = this.parseTime(this.getMarkerTime(marker));
        this.jumpTo(block, seconds, marker, markers);
      });
    });
  }

  play(block) {
    const video = block.querySelector('video');
    const iframe = block.querySelector('iframe');
    const videoHasSrc = this.hasVideoSource(video);
    const iframeHasSrc = this.hasEmbedSource(iframe);

    if (videoHasSrc) {
      this.playNative(block, video);
      return;
    }

    if (iframeHasSrc) {
      this.playEmbed(block, iframe);
      return;
    }

    if (video) {
      this.playNative(block, video);
      return;
    }

    if (iframe) {
      this.playEmbed(block, iframe);
    }
  }

  playNative(block, video) {
    block.classList.add('is-playing');
    video.muted = false;
    video.removeAttribute('muted');

    const promise = video.play();
    if (promise && typeof promise.catch === 'function') {
      promise.catch(() => {});
    }
  }

  playEmbed(block, iframe) {
    const raw = iframe.getAttribute('src') || iframe.getAttribute('data-src') || '';
    if (!raw) {
      return;
    }

    block.classList.add('is-playing');
    iframe.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture');

    try {
      const url = new URL(raw, window.location.origin);
      const host = url.hostname;

      if (host.includes('vimeo.com')) {
        url.searchParams.set('autoplay', '1');
        url.searchParams.delete('muted');
        url.searchParams.delete('background');
      } else if (host.includes('youtube.com') || host.includes('youtu.be')) {
        url.searchParams.set('autoplay', '1');
        url.searchParams.set('playsinline', '1');
      } else {
        url.searchParams.set('autoplay', '1');
      }

      iframe.src = url.toString();
    } catch (error) {
      const separator = raw.includes('?') ? '&' : '?';
      iframe.src = `${raw}${separator}autoplay=1`;
    }
  }

  hasVideoSource(video) {
    if (!video) {
      return false;
    }

    if (video.currentSrc) {
      return true;
    }

    const attrSrc = video.getAttribute('src');
    if (attrSrc) {
      return true;
    }

    const source = video.querySelector('source');
    return !!(source && source.getAttribute('src'));
  }

  hasEmbedSource(iframe) {
    if (!iframe) {
      return false;
    }

    return !!(iframe.getAttribute('src') || iframe.getAttribute('data-src'));
  }

  jumpTo(block, seconds, activeMarker, markers) {
    const video = block.querySelector('video');
    if (!video || Number.isNaN(seconds) || seconds < 0) {
      return;
    }

    this.playNative(block, video);

    const seek = () => {
      video.currentTime = seconds;
    };

    if (video.readyState >= 1) {
      seek();
    } else {
      video.addEventListener('loadedmetadata', seek, { once: true });
    }

    markers.forEach((marker) => {
      marker.classList.toggle('is-active', marker === activeMarker);
    });
  }

  syncActiveMarker(video, markers) {
    if (!markers.length) {
      return;
    }

    const times = markers.map((marker) => ({
      marker,
      time: this.parseTime(this.getMarkerTime(marker)),
    })).filter((entry) => !Number.isNaN(entry.time));

    if (!times.length) {
      return;
    }

    times.sort((a, b) => a.time - b.time);

    let active = times[0].marker;
    times.forEach((entry) => {
      if (video.currentTime >= entry.time) {
        active = entry.marker;
      }
    });

    markers.forEach((marker) => {
      marker.classList.toggle('is-active', marker === active);
    });
  }

  getMarkerTime(marker) {
    const timeEl = marker.querySelector('.lc-chapter-video-marker__time');
    return timeEl ? timeEl.textContent.trim() : '';
  }

  parseTime(value) {
    if (!value) {
      return NaN;
    }

    const cleaned = String(value).trim();

    if (/^\d+(\.\d+)?$/.test(cleaned)) {
      return parseFloat(cleaned);
    }

    const parts = cleaned.split(':').map((part) => parseFloat(part));
    if (parts.some((part) => Number.isNaN(part))) {
      return NaN;
    }

    if (parts.length === 3) {
      return (parts[0] * 3600) + (parts[1] * 60) + parts[2];
    }

    if (parts.length === 2) {
      return (parts[0] * 60) + parts[1];
    }

    return NaN;
  }
}

export default ChapterVideo;
